
import { useState, useEffect } from 'react';
import { Assessment } from '@/types/assessment';
import { getAssessments } from '@/services/assessment'; 
import { archiveAssessment, restoreArchivedAssessment } from '@/services/assessment';
import { useToast } from '@/hooks/use-toast';

export function useAssessments() {
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [filteredAssessments, setFilteredAssessments] = useState<Assessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { toast } = useToast();
  
  useEffect(() => {
    const fetchAssessments = async () => {
      try {
        const data = await getAssessments();
        setAssessments(data);
        setFilteredAssessments(data);
      } catch (error) {
        console.error("Error fetching assessments:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load assessments. Please try again."
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchAssessments();
  }, [toast]);
  
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredAssessments(assessments);
    } else {
      const filtered = assessments.filter(assessment => 
        assessment.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        assessment.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredAssessments(filtered);
    }
  }, [searchTerm, assessments]);
  
  const handleArchive = async (id: string) => {
    try {
      await archiveAssessment(id);
      setAssessments(prev => 
        prev.map(assessment => 
          assessment.id === id ? { ...assessment, isActive: false } : assessment
        )
      );
      toast({
        title: "Assessment Archived",
        description: "The assessment has been archived successfully."
      });
    } catch (error) {
      console.error("Error archiving assessment:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to archive assessment. Please try again."
      });
    }
  };

  const handleUnarchive = async (id: string) => {
    try {
      await restoreArchivedAssessment(id);
      setAssessments(prev => 
        prev.map(assessment => 
          assessment.id === id ? { ...assessment, isActive: true } : assessment
        )
      );
      toast({
        title: "Assessment Restored",
        description: "The assessment has been restored successfully."
      });
    } catch (error) {
      console.error("Error restoring assessment:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to restore assessment. Please try again."
      });
    }
  };

  return {
    assessments: filteredAssessments,
    loading,
    searchTerm,
    setSearchTerm,
    handleArchive,
    handleUnarchive
  };
}
